import { Link } from 'react-router-dom';
import { Crown, TrendingUp, Target, Award, Trophy, Gamepad2, Star, Zap, Percent, ChevronRight } from 'lucide-react';

interface FeaturedPlayer {
  id: string;
  name: string;
  slug: string;
  avatar: string | null;
  points: number;
  wins: number;
  goals: number;
  gameId?: string | null;
  draws?: number;
  losses?: number;
  matches?: number;
}

interface FeaturedPlayerCardProps {
  player: FeaturedPlayer | null;
}

export function FeaturedPlayerCard({ player }: FeaturedPlayerCardProps) {
  if (!player) return null;

  const totalMatches = (player.wins || 0) + (player.draws || 0) + (player.losses || 0);
  const winRate = totalMatches > 0 
    ? Math.round((player.wins / totalMatches) * 100) 
    : 0;

  return (
    <Link 
      to={`/players/${player.slug}`}
      className="block group"
    >
      <div className="featured-player-unique rounded-2xl overflow-hidden relative hover:scale-[1.01] transition-all duration-300">
        {/* Animated gradient border */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-accent rounded-2xl animate-gradient-shift" />
        
        {/* Inner content */}
        <div className="relative m-[2px] bg-card/95 backdrop-blur-xl rounded-2xl p-4 md:p-5">
          {/* Header with badge */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30">
                <Award className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display text-sm md:text-base font-bold text-foreground">Player of the Week</h3>
                <p className="text-[10px] md:text-xs text-muted-foreground">Top Performer</p>
              </div>
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-success/20 border border-success/30">
              <Percent className="h-3 w-3 text-success" />
              <span className="text-xs font-bold text-success">{winRate}% WR</span>
            </div>
          </div>
          
          {/* Main content - Horizontal layout */}
          <div className="flex gap-4">
            {/* Avatar Section */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 border-primary/50 shadow-lg shadow-primary/20">
                {player.avatar ? (
                  <img 
                    src={player.avatar} 
                    alt={player.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/20 flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-display font-bold text-primary">{player.name.charAt(0)}</span>
                  </div>
                )}
              </div>
              {/* Crown badge */}
              <div className="absolute -top-2 -right-2 p-1.5 rounded-full bg-gold text-yellow-900 shadow-lg shadow-gold/40">
                <Crown className="h-3.5 w-3.5" />
              </div>
            </div>
            
            {/* Player Info & Stats */}
            <div className="flex-1 min-w-0">
              {/* Name & Game ID */}
              <h4 className="font-display text-lg md:text-xl font-bold text-foreground truncate">{player.name}</h4>
              {player.gameId && (
                <p className="text-xs text-muted-foreground font-mono flex items-center gap-1 mb-3">
                  <Gamepad2 className="h-3 w-3" />
                  {player.gameId}
                </p>
              )}
              
              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 rounded-lg bg-gold/10 border border-gold/20">
                  <Star className="h-3.5 w-3.5 text-gold mx-auto mb-1" />
                  <span className="text-sm font-bold text-foreground block">{player.points}</span>
                  <span className="text-[8px] text-muted-foreground uppercase">Points</span>
                </div>
                <div className="text-center p-2 rounded-lg bg-success/10 border border-success/20">
                  <Trophy className="h-3.5 w-3.5 text-success mx-auto mb-1" />
                  <span className="text-sm font-bold text-foreground block">{player.wins}</span>
                  <span className="text-[8px] text-muted-foreground uppercase">Wins</span>
                </div>
                <div className="text-center p-2 rounded-lg bg-primary/10 border border-primary/20">
                  <Zap className="h-3.5 w-3.5 text-primary mx-auto mb-1" />
                  <span className="text-sm font-bold text-foreground block">{player.goals}</span>
                  <span className="text-[8px] text-muted-foreground uppercase">Goals</span>
                </div>
                <div className="text-center p-2 rounded-lg bg-muted/50 border border-border/30">
                  <Target className="h-3.5 w-3.5 text-muted-foreground mx-auto mb-1" />
                  <span className="text-sm font-bold text-foreground block">{totalMatches}</span>
                  <span className="text-[8px] text-muted-foreground uppercase">Matches</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Win Rate Progress Bar */}
          <div className="mt-4 p-3 rounded-xl bg-muted/30 border border-border/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Performance
              </span>
              <div className="flex gap-3 text-xs">
                <span className="text-success font-medium">{player.wins}W</span>
                <span className="text-warning font-medium">{player.draws || 0}D</span>
                <span className="text-destructive font-medium">{player.losses || 0}L</span>
              </div>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden flex">
              {totalMatches > 0 && (
                <>
                  <div 
                    className="h-full bg-success transition-all duration-500" 
                    style={{ width: `${(player.wins / totalMatches) * 100}%` }} 
                  />
                  <div 
                    className="h-full bg-warning transition-all duration-500" 
                    style={{ width: `${((player.draws || 0) / totalMatches) * 100}%` }} 
                  />
                  <div 
                    className="h-full bg-destructive transition-all duration-500" 
                    style={{ width: `${((player.losses || 0) / totalMatches) * 100}%` }} 
                  />
                </>
              )}
            </div>
          </div>
          
          {/* View Profile CTA */}
          <div className="mt-3 flex items-center justify-end">
            <span className="text-xs text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              View Full Profile <ChevronRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
